import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import ReactPlayer from 'react-player';

import { imagesURL } from 'config';
import { formatHtmlForPlay } from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { WhiteBox } from 'components/flink-play';
import classes from '../../../components/flink-play/EbookComponent/EbookComponent.module.scss';
import { EBOOK_WIDTH, EBOOK_HEIGHT } from 'consts/ebook-consts';

const prevBtnImg = `${imagesURL}/TemplateGraphics/e-Book/arrow_left.png`;
const nextBtnImg = `${imagesURL}/TemplateGraphics/e-Book/arrow_right.png`;
const replayBtnImg = `${imagesURL}/Images/Audio/audio_replay.png`;
const MOBILE_TEXT_SCALE = 1.5;

const getPercents = (size, base) => {
  if (size.indexOf && size.indexOf('%') !== -1) return size;
  return (parseFloat(size) / base) * 100 + '%';
};

const mobileTextScrollPositions = new Map();

const getMeaningfulOverflowThreshold = (el) => {
  if (!el || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return 24;
  }

  const computed = window.getComputedStyle(el);
  const fontSize = parseFloat(computed.fontSize) || 20;
  const parsedLineHeight = parseFloat(computed.lineHeight);
  const lineHeight = Number.isFinite(parsedLineHeight) ? parsedLineHeight : fontSize * 1.42;

  // The mobile text frame can have a few pixels of technical overflow because
  // authored dimensions are scaled to the device. The cue means "there is
  // another readable line below", not "the browser can scroll by a few px".
  return Math.max(18, lineHeight * 0.75);
};

class MobileTextBlock extends Component {
  frameRef = React.createRef();
  scrollRef = React.createRef();
  contentRef = React.createRef();
  restoreRaf = null;
  restoreTimer = null;
  nativeGuardEvents = [
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    'pointerdown',
    'pointermove',
    'pointerup',
    'pointercancel',
    'mousedown',
    'mouseup',
    'click',
    'wheel',
  ];

  componentDidMount() {
    this.bindNativeScrollGuards();
    document.addEventListener('ebookPageFlipDomUpdated', this.handlePageFlipDomUpdated);
    window.addEventListener('resize', this.scheduleRestoreScrollPosition);
    this.scheduleRestoreScrollPosition();
  }

  componentDidUpdate() {
    this.scheduleRestoreScrollPosition();
  }

  componentWillUnmount() {
    this.rememberScrollPosition();
    this.unbindNativeScrollGuards();
    document.removeEventListener('ebookPageFlipDomUpdated', this.handlePageFlipDomUpdated);
    window.removeEventListener('resize', this.scheduleRestoreScrollPosition);
    if (this.restoreRaf) cancelAnimationFrame(this.restoreRaf);
    if (this.restoreTimer) clearTimeout(this.restoreTimer);
  }

  isScrollable = () => {
    const el = this.scrollRef.current;
    if (!el) return false;

    // Gesture ownership must follow the browser's real scroll range, not the
    // visual affordance threshold. A text box with even a small real overflow
    // still needs to keep its vertical gesture away from react-pageflip.
    return Math.max(0, el.scrollHeight - el.clientHeight) > 1;
  };

  getScrollKey = () => String(this.props.scrollKey || '');

  getFlipRoot = () => {
    const el = this.scrollRef.current;
    return el && el.closest ? el.closest('.ebookFlipBookRoot') : null;
  };

  rememberScrollPosition = () => {
    const el = this.scrollRef.current;
    if (!el) return;
    mobileTextScrollPositions.set(this.getScrollKey(), el.scrollTop || 0);
  };

  syncScrollPositionToCopies = (scrollTop) => {
    const root = this.getFlipRoot();
    const own = this.scrollRef.current;
    const key = this.getScrollKey();
    if (!key) return;

    const nodes = root
      ? Array.from(root.querySelectorAll('[data-ebook-text-scroll-key]'))
      : (own ? [own] : []);

    nodes.forEach((node) => {
      if (!node || !node.dataset || node.dataset.ebookTextScrollKey !== key) return;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      const nextTop = Math.max(0, Math.min(Number(scrollTop) || 0, maxScroll));
      if (Math.abs((node.scrollTop || 0) - nextTop) > 1) {
        node.scrollTop = nextTop;
      }
    });
  };

  getTextOverflowMetrics = () => {
    const el = this.scrollRef.current;
    const content = this.contentRef.current;
    if (!el || !content || el.clientHeight <= 1) {
      return { maxTextScroll: 0, remainingText: 0 };
    }

    const nativeMaxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    if (nativeMaxScroll <= 1) {
      return { maxTextScroll: 0, remainingText: 0 };
    }

    const computed = typeof window !== 'undefined' && window.getComputedStyle
      ? window.getComputedStyle(el)
      : null;
    const paddingBottom = computed ? parseFloat(computed.paddingBottom) || 0 : 0;
    const scrollRect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    const viewportBottom = Math.max(0, el.clientHeight - paddingBottom);
    let renderedTextBottom = 0;

    // Measure actual rendered glyphs instead of content.scrollHeight. Authored
    // eBook HTML can contain empty paragraphs/trailing line boxes that make the
    // browser technically scrollable even when every visible text line already
    // fits. Those empty boxes were the reason the grey down cue appeared on short
    // pages such as Nurse Rosa and Sammy Gets a Ride.
    if (
      scrollRect
      && typeof document !== 'undefined'
      && typeof document.createTreeWalker === 'function'
      && typeof document.createRange === 'function'
    ) {
      const showText = typeof NodeFilter !== 'undefined' ? NodeFilter.SHOW_TEXT : 4;
      const walker = document.createTreeWalker(content, showText, null, false);
      let node = walker.nextNode();

      while (node) {
        if (String(node.nodeValue || '').trim()) {
          const range = document.createRange();
          range.selectNodeContents(node);
          const rects = Array.from(range.getClientRects());
          for (let rectIndex = 0; rectIndex < rects.length; rectIndex += 1) {
            const rect = rects[rectIndex];
            if (rect && (rect.width || rect.height)) {
              renderedTextBottom = Math.max(
                renderedTextBottom,
                rect.bottom - scrollRect.top + (el.scrollTop || 0)
              );
            }
          }
          if (typeof range.detach === 'function') range.detach();
        }
        node = walker.nextNode();
      }
    }

    // Defensive fallback for unusual WebViews that do not expose Range rects.
    if (!renderedTextBottom) {
      const contentRect = content.getBoundingClientRect ? content.getBoundingClientRect() : null;
      renderedTextBottom = contentRect && scrollRect
        ? contentRect.bottom - scrollRect.top + (el.scrollTop || 0)
        : content.scrollHeight || 0;
    }

    const measuredTextOverflow = Math.max(0, renderedTextBottom - viewportBottom);
    const maxTextScroll = Math.min(nativeMaxScroll, measuredTextOverflow);
    const currentScroll = Math.max(0, Math.min(el.scrollTop || 0, maxTextScroll));

    return {
      maxTextScroll,
      remainingText: Math.max(0, maxTextScroll - currentScroll),
    };
  };

  updateScrollAffordance = () => {
    const el = this.scrollRef.current;
    const frame = this.frameRef.current;
    if (!el || !frame) return;

    const meaningfulOverflowThreshold = getMeaningfulOverflowThreshold(el);
    const { maxTextScroll, remainingText } = this.getTextOverflowMetrics();
    const scrollable = maxTextScroll > meaningfulOverflowThreshold;
    const hasMoreText = scrollable && remainingText > meaningfulOverflowThreshold;

    frame.dataset.scrollable = scrollable ? 'true' : 'false';
    frame.dataset.hasMoreText = hasMoreText ? 'true' : 'false';

    // Do not let tiny authored/scaling overflow turn a short 1-2 line caption
    // into a scrollable control. Keep it anchored at its authored start.
    if (!scrollable && Math.abs(el.scrollTop || 0) > 1) {
      mobileTextScrollPositions.set(this.getScrollKey(), 0);
      this.syncScrollPositionToCopies(0);
    }
  };

  restoreScrollPosition = () => {
    const el = this.scrollRef.current;
    if (!el) return;

    const saved = mobileTextScrollPositions.get(this.getScrollKey());
    if (typeof saved === 'number') {
      this.syncScrollPositionToCopies(saved);
    }
    this.updateScrollAffordance();
  };

  scheduleRestoreScrollPosition = () => {
    if (this.restoreRaf) cancelAnimationFrame(this.restoreRaf);
    if (this.restoreTimer) clearTimeout(this.restoreTimer);

    this.restoreRaf = requestAnimationFrame(() => {
      this.restoreRaf = null;
      this.restoreScrollPosition();
    });

    // react-pageflip refreshes HTML pages from a passive effect. Restore again
    // after that effect so WebView cannot leave the text at scrollTop=0.
    this.restoreTimer = setTimeout(() => {
      this.restoreTimer = null;
      this.restoreScrollPosition();
    }, 0);
  };

  handlePageFlipDomUpdated = () => {
    this.scheduleRestoreScrollPosition();
  };

  stopPageFlipForScrollableText = (event) => {
    if (!this.isScrollable()) return;

    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }

    // Do not restore on touchend: iOS momentum scrolling continues after the
    // finger is released. Restoring here would jump the text back mid-scroll.
    if (event && ['touchend', 'touchcancel', 'pointerup', 'pointercancel', 'mouseup'].includes(event.type)) {
      this.rememberScrollPosition();
    }
  };

  bindNativeScrollGuards = () => {
    const el = this.scrollRef.current;
    if (!el) return;

    this.nativeGuardEvents.forEach((eventName) => {
      el.addEventListener(eventName, this.stopPageFlipForScrollableText, { passive: true });
    });
  };

  unbindNativeScrollGuards = () => {
    const el = this.scrollRef.current;
    if (!el) return;

    this.nativeGuardEvents.forEach((eventName) => {
      el.removeEventListener(eventName, this.stopPageFlipForScrollableText, false);
    });
  };

  handleScroll = () => {
    const el = this.scrollRef.current;
    if (!el) return;

    mobileTextScrollPositions.set(this.getScrollKey(), el.scrollTop || 0);
    this.syncScrollPositionToCopies(el.scrollTop || 0);
    this.updateScrollAffordance();
  };

  render() {
    const { frameStyle, html, scrollKey } = this.props;

    return (
      <div ref={this.frameRef} className={classes.mobileTextFrame} style={frameStyle}>
        <div
          ref={this.scrollRef}
          className={classnames('ql-editor', classes.text, classes.mobileTextScrollArea)}
          data-ebook-text-scroll="true"
          data-ebook-text-scroll-key={String(scrollKey || '')}
          onScroll={this.handleScroll}
        >
          <div ref={this.contentRef} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        <span className={classes.mobileTextMoreIndicator} aria-hidden="true" />
      </div>
    );
  }
}

MobileTextBlock.propTypes = {
  frameStyle: PropTypes.object.isRequired,
  html: PropTypes.string.isRequired,
  scrollKey: PropTypes.string.isRequired,
};

class MobileEbookComponent extends Component {
  constructor(props) {
    super(props);

    const { smallWidth, largeWidth } = props;
    const portraitEbookStyles = {
      width: smallWidth + 'vmin',
      height: (EBOOK_HEIGHT / EBOOK_WIDTH) * smallWidth + 'vmin',
    };
    const landscapeEbookStyles = {
      width: largeWidth + 'vmin',
      height: (EBOOK_HEIGHT / EBOOK_WIDTH) * largeWidth + 'vmin',
    };

    this.state = {
      ebookStyles: portraitEbookStyles,
      landscapeEbookStyles,
      portraitEbookStyles,
    };
  }

  componentDidMount() {
    this.onChangeAspectRatio();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.aspectRatio !== this.props.aspectRatio) {
      this.onChangeAspectRatio();
    }
  }

  onImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    let width = (naturalWidth / EBOOK_WIDTH) * 100;
    let height = (naturalHeight / EBOOK_HEIGHT) * 100;

    if (width > 100 && width > height) {
      width = '100%';
      height = 'auto';
    } else if (height > 100 && height > width) {
      height = '100%';
      width = 'auto';
    } else {
      height = height + '%';
      width = width + '%';
    }

    this.setState({ imageStyles: { width, height } });
  };

  onChangeAspectRatio = () => {
    const { aspectRatio } = this.props;
    const { landscapeEbookStyles, portraitEbookStyles } = this.state;
    this.setState({
      ebookStyles: aspectRatio >= 1.25 ? landscapeEbookStyles : portraitEbookStyles,
    });
  };

  onVideoEnded = () => {
    const { onVideoEnded } = this.props;
    onVideoEnded && onVideoEnded();
  };

  videoRef = React.createRef();

  replayVideo = () => {
    const { replayHandler } = this.props;
    const reactPlayer = this.videoRef.current;
    if (!reactPlayer || !replayHandler) return;
    reactPlayer.seekTo(0);
    replayHandler();
  };

  stopNavGesturePropagation = (event) => {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
  };

  runNavHandler = (event, handler) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    if (typeof handler === 'function') handler();
  };

  render() {
    const { imageStyles, ebookStyles } = this.state;
    const {
      page,
      assetsFolder,
      prevClickHandler,
      nextClickHandler,
      videoPlaying = false,
      wrapperStyles = {},
      showVideReplayBtn,
      showPrev = false,
      showNext = false,
    } = this.props;
    const { image, video, text = [] } = page || {};
    const imageUrl = image && (/^data:/i.test(image) ? image : assetsFolder + image);
    const videoUrl = video && (/^data:/i.test(video) ? video : assetsFolder + video);
    const mobilePlatform = getMobileAppPlatform();
    const renderedImageStyles = mobilePlatform === 'ios'
      ? {
        ...(imageStyles || {}),
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
      }
      : imageStyles;

    return (
      <WhiteBox outerClass={classes.ebookWrapper} outerStyle={wrapperStyles}>
        <div className={classes.ebook} style={ebookStyles}>
          {image && (
            <img
              style={renderedImageStyles}
              onLoad={this.onImageLoad}
              className={classnames(classes.ebookImage, classes.mobilePageFlipImage)}
              src={imageUrl}
              alt=""
            />
          )}

          {video && (
            <ReactPlayer
              ref={this.videoRef}
              playing={videoPlaying}
              width="100%"
              height="100%"
              url={videoUrl}
              onEnded={this.onVideoEnded}
            />
          )}

          {video && showVideReplayBtn && (
            <button className={classes.replayBtn} onClick={this.replayVideo}>
              <img src={replayBtnImg} alt="Replay" />
            </button>
          )}

          {showPrev && (
            <button
              type="button"
              onMouseDown={this.stopNavGesturePropagation}
              onTouchStart={this.stopNavGesturePropagation}
              onPointerDown={this.stopNavGesturePropagation}
              onClick={(event) => this.runNavHandler(event, prevClickHandler)}
              className={classnames(classes.navBtn, classes.prevBtn)}
            >
              <img src={prevBtnImg} alt="Previous" />
            </button>
          )}

          {showNext && (
            <button
              type="button"
              onMouseDown={this.stopNavGesturePropagation}
              onTouchStart={this.stopNavGesturePropagation}
              onPointerDown={this.stopNavGesturePropagation}
              onClick={(event) => this.runNavHandler(event, nextClickHandler)}
              className={classnames(classes.navBtn, classes.nextBtn)}
            >
              <img src={nextBtnImg} alt="Next" />
            </button>
          )}

          {text.map((t, textIndex) => {
            if (!t) return null;
            const { width, height, left, top } = t.dim;
            const html = formatHtmlForPlay({
              html: t.text,
              calc: (size) => {
                return (
                  ((size / ((EBOOK_HEIGHT / parseFloat(ebookStyles.height)) * 100)) * 100) *
                  MOBILE_TEXT_SCALE
                );
              },
            });

            return (
              <MobileTextBlock
                key={t.id}
                frameStyle={{
                  width: getPercents(width, EBOOK_WIDTH),
                  height: getPercents(height, EBOOK_HEIGHT),
                  left: getPercents(left, EBOOK_WIDTH),
                  top: getPercents(top, EBOOK_HEIGHT),
                }}
                html={html}
                scrollKey={`${assetsFolder}|${page._id || page.id || image || 'page'}|${t.id || textIndex}`}
              />
            );
          })}
        </div>
      </WhiteBox>
    );
  }
}

MobileEbookComponent.defaultProps = {
  smallWidth: 94,
  largeWidth: 113,
};

MobileEbookComponent.propTypes = {
  aspectRatio: PropTypes.number,
  smallWidth: PropTypes.number,
  largeWidth: PropTypes.number,
  page: PropTypes.object.isRequired,
  assetsFolder: PropTypes.string.isRequired,
  prevClickHandler: PropTypes.func,
  nextClickHandler: PropTypes.func,
  showPrev: PropTypes.bool,
  showNext: PropTypes.bool,
};

const mapStateToProps = ({ status: { aspectRatio } }) => ({ aspectRatio });

export default connect(mapStateToProps)(MobileEbookComponent);
